import { Chain } from './Chain'
import { NftContract } from './NftContract'
import TrophyNft from '~/abi/TrophyNft.json'
import CardNft from '~/abi/CardNft.json'
import LeveradeNft from '~/abi/LeveradeNft.json'
import ChainProviderDomainException from '~/modules/Chains/Domain/ChainProviderDomainException'

/**
 * Provide the Chain where we're connected
 */
export class ChainProvider {
  public static readonly avalancheId = 43114
  public static readonly avalancheName = 'Avalanche C-Chain'
  public static readonly avalancheRpcUri = 'https://api.avax.network/ext/bc/C/rpc'
  public static readonly avalancheTrophyContractAddress = '0x10799EE6A1914e487935C37a7D383B5755e478e2'
  public static readonly avalancheQueenCupContractAddress = '0xfb917E76927e2BED4361bAa088c6e680E8546c4B'
  public static readonly avalancheCardContractAddress = '0xdbeEfBFaf9A9a1Bb64E814aB1B0cc261DeC4a083'

  public static readonly fujiId = 43113
  public static readonly fujiName = 'Avalanche FUJI C-Chain'
  public static readonly fujiRpcUri = 'https://api.avax-test.network/ext/bc/C/rpc'
  public static readonly fujiTrophyContractAddress = '0x4331555CF00DF056dDCE67149F2236B2C417bC02'
  public static readonly fujiQueenCupContractAddress = '0xF35908524C9273F9C24380D506504388B8789564'
  public static readonly fujiCardContractAddress = '0x7C6F930B396Fd6ff955A101c9129fa45fA1D7982'

  public static readonly cronosTestnetId = 338
  public static readonly cronosTestnetName = 'Cronos Testnet'
  public static readonly cronosTestnetRpcUri = 'https://evm-t3.cronos.org'
  public static readonly cronosTestnetTrophyContractAddress = '0x000000000000000000000000000000000000dEaD'
  public static readonly cronosTestnetQueenCupContractAddress = '0x000000000000000000000000000000000000dEaD'
  public static readonly cronosTestnetCardContractAddress = '0x000000000000000000000000000000000000dEaD'

  public static readonly polygonId = 137
  public static readonly polygonName = 'Polygon'
  public static readonly polygonRpcUri = 'https://rpc-polygon.matic.network'
  public static readonly polygonTrophyContractAddress = '0x5ef009F059ebf90671ea806821F0e6D2F9C07700'
  public static readonly polygonQueenCupContractAddress = '0xfb917E76927e2BED4361bAa088c6e680E8546c4B'
  public static readonly polygonCardContractAddress = '0x4BBd55B31fb729A0613b6d6ae3Ef509EEb6086D9'

  public static readonly mumbaiId = 80001
  public static readonly mumbaiName = 'Polygon Mumbai Testnet'
  public static readonly mumbaiRpcUri = 'https://rpc-mumbai.matic.network'
  public static readonly mumbaiTrophyContractAddress = '0xf4A71F874B9F6Ce429df7BF196553847603bE652'
  public static readonly mumbaiQueenCupContractAddress = '0xF35908524C9273F9C24380D506504388B8789564'
  public static readonly mumbaiCardContractAddress = '0x4958Fe117f1fcB734146D095eCF0B64Bc8d45558'

  public static readonly amoyId = 80002
  public static readonly amoyName = 'Polygon Amoy Testnet'
  public static readonly amoyRpcUri = 'https://rpc-amoy.polygon.technology'
  public static readonly amoyTrophyContractAddress = '0x000000000000000000000000000000000000dEaD'
  public static readonly amoyQueenCupContractAddress = '0x000000000000000000000000000000000000dEaD'
  public static readonly amoyCardContractAddress = '0x000000000000000000000000000000000000dEaD'

  public static readonly hardhatId = 1337
  public static readonly hardhatName = 'Hardhat'
  public static readonly hardhatRpcUri = 'http://localhost:8545'
  public static readonly hardhatTrophyContractAddress = '0x5FbDB2315678afecb367f032d93F642f64180aa3'
  public static readonly hardhatQueenCupContractAddress = '0x000000000000000000000000000000000000dEaD'
  public static readonly hardhatCardContractAddress = '0xe7f1725E7734CE288F8367e1Bb143E90bb3F0512'

  /**
   * Build a Chain object according to the given NEXT_PUBLIC_CHAIN_ID env var
   * @returns Chain
   * @throws NoChainProvided NEXT_PUBLIC_CHAIN_ID is not defined
   * @throws ChainNotSupported NEXT_PUBLIC_CHAIN_ID given but not supported
   */
  static build (): Chain {
    if (!process.env.NEXT_PUBLIC_CHAIN_ID) {
      throw ChainProviderDomainException.noChainProvided()
    }

    const chainId = parseInt(process.env.NEXT_PUBLIC_CHAIN_ID)

    switch (chainId) {
      case ChainProvider.polygonId:
        return new Chain(
          ChainProvider.polygonId,
          ChainProvider.polygonName,
          ChainProvider.polygonRpcUri,
          new NftContract(ChainProvider.polygonTrophyContractAddress, TrophyNft.abi),
          new NftContract(ChainProvider.polygonCardContractAddress, CardNft.abi),
          new NftContract(ChainProvider.polygonQueenCupContractAddress, LeveradeNft.abi)
        )

      case ChainProvider.amoyId:
        return new Chain(
          ChainProvider.amoyId,
          ChainProvider.amoyName,
          ChainProvider.amoyRpcUri,
          new NftContract(ChainProvider.amoyTrophyContractAddress, TrophyNft.abi),
          new NftContract(ChainProvider.amoyCardContractAddress, CardNft.abi),
          new NftContract(ChainProvider.amoyQueenCupContractAddress, LeveradeNft.abi)
        )

      case ChainProvider.cronosTestnetId:
        return new Chain(
          ChainProvider.cronosTestnetId,
          ChainProvider.cronosTestnetName,
          ChainProvider.cronosTestnetRpcUri,
          new NftContract(ChainProvider.cronosTestnetTrophyContractAddress, TrophyNft.abi),
          new NftContract(ChainProvider.cronosTestnetCardContractAddress, CardNft.abi),
          new NftContract(ChainProvider.cronosTestnetQueenCupContractAddress, LeveradeNft.abi)
        )

      case ChainProvider.mumbaiId:
        return new Chain(
          ChainProvider.mumbaiId,
          ChainProvider.mumbaiName,
          ChainProvider.mumbaiRpcUri,
          new NftContract(ChainProvider.mumbaiTrophyContractAddress, TrophyNft.abi),
          new NftContract(ChainProvider.mumbaiCardContractAddress, CardNft.abi),
          new NftContract(ChainProvider.mumbaiQueenCupContractAddress, LeveradeNft.abi)
        )

      case ChainProvider.hardhatId:
        return new Chain(
          ChainProvider.hardhatId,
          ChainProvider.hardhatName,
          ChainProvider.hardhatRpcUri,
          new NftContract(ChainProvider.hardhatTrophyContractAddress, TrophyNft.abi),
          new NftContract(ChainProvider.hardhatCardContractAddress, CardNft.abi),
          new NftContract(ChainProvider.hardhatQueenCupContractAddress, TrophyNft.abi)
        )

      case ChainProvider.avalancheId:
        return new Chain(
          ChainProvider.avalancheId,
          ChainProvider.avalancheName,
          ChainProvider.avalancheRpcUri,
          new NftContract(ChainProvider.avalancheTrophyContractAddress, TrophyNft.abi),
          new NftContract(ChainProvider.avalancheCardContractAddress, CardNft.abi),
          new NftContract(ChainProvider.avalancheQueenCupContractAddress, LeveradeNft.abi)
        )

      case ChainProvider.fujiId:
        return new Chain(
          ChainProvider.fujiId,
          ChainProvider.fujiName,
          ChainProvider.fujiRpcUri,
          new NftContract(ChainProvider.fujiTrophyContractAddress, TrophyNft.abi),
          new NftContract(ChainProvider.fujiCardContractAddress, CardNft.abi),
          new NftContract(ChainProvider.fujiQueenCupContractAddress, LeveradeNft.abi)
        )

      default:
        throw ChainProviderDomainException.chainNotSupported(chainId)
    }
  }

  static getName (chainId: number): string {
    switch (chainId) {
      case ChainProvider.polygonId:
        return ChainProvider.polygonName

      case ChainProvider.amoyId:
        return ChainProvider.amoyName

      case ChainProvider.cronosTestnetId:
        return ChainProvider.cronosTestnetName

      case ChainProvider.mumbaiId:
        return ChainProvider.mumbaiName

      case ChainProvider.hardhatId:
        return ChainProvider.hardhatName

      case ChainProvider.avalancheId:
        return ChainProvider.avalancheName

      case ChainProvider.fujiId:
        return ChainProvider.fujiName

      default:
        throw ChainProviderDomainException.chainNotSupported(chainId)
    }
  }
}

export default ChainProvider.build()
